'use client'

import { FunctionComponent, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { ModalContext, ModalContextType } from '../../context/ModalContext'
import { useIsMobileOrTablet } from '../../utils/screen.utils'
import MyMoveInformation from '../InformationFrame/Product/MyMoveInformation'
import ModalCart from '../Modal/ModalCart'
import { useRouter } from 'next/navigation'
import ProductConfigurationCarving from './ProductConfigurationCarving'
import ProductPaymentWidgets from '../ProductPaymentWidgets/ProductPaymentWidgets'
import {
  BRACELET_MY_MOVE_COLOR_ATTRIBUTE_NAME,
  BRACELET_MY_MOVE_SIZE_ATTRIBUTE_NAME,
  getProductConfigurationDefaultValues,
  ProductConfigurationFormData,
} from './ProductConfiguration.utils'
import ProductConfigurationSelectConfigurableOption from './ProductConfigurationSelectConfigurableOption'
import ProductConfigurationBraceletMyMove from './ProductConfigurationBraceletMyMove'
import {
  findVariant,
  getIsInStock,
  getMyMoveCustomImage,
  getUrlOfProductLinkOption,
  mapCmsProductsToProducts,
  sortImageGalleryOfProduct,
} from '../../utils/Product.utils'
import ProductConfigurationCtaSimpleProduct from './ProductConfigurationCta/ProductConfigurationCtaSimpleProduct'
import ProductConfigurationCtaConfigurableProduct from './ProductConfigurationCta/ProductConfigurationCtaConfigurableProduct'
import ProductConfigurationCtaBundleProduct from './ProductConfigurationCta/ProductConfigurationCtaBundleProduct'
import ProductConfigurationPrice, {
  getPriceOfSelectedVariantsForBundle,
  ProductConfigurationPriceBundleProduct,
} from './ProductConfigurationPrice'
import ProductConfigurationBundleProduct, {
  getSelectedVariantsForBundleProduct,
} from './ProductConfigurationBundleProduct'
import { AssetType } from '../ProductAssets/ProductAssets.types'
import ProductAssets from '../ProductAssets/ProductAssets'
import Wishlist from './Wishlist'
import RecentViewedProducts from './RecentViewedProducts'
import ListSelection from '../ListSelection/ListSelection'
import BreadCrumb from '../BreadCrumb/Breadcrumb'
import Layout from '../Layout/Layout'
import { createGtmProduct, gtmCategory, gtmPush } from '../../utils/gtmHelpers'
import { convertImageToAssetType } from '../../utils/media.utils'
import CustomSelect from '../Form/InputWrapper/CustomSelect'
import { AlertNotificationContext } from '../../context/AlertNotificationContext'
import useAddProductToCart, {
  AddMyMoveToCartOption,
  buildMyMoveOptionToCart,
  useAddMyMoveProductToCart,
} from '../../../core/hook/cartHooks/useAddProductToCart'
import { ConfigContext, useTranslation } from '../../context/ConfigContext'
import { buildFullUrlMedia, handleError } from '../../../core/hook/utils'
import { useQuery } from '../../../core/hook/useQuery'
import { ProductClientDocument } from '../../../queries/ProductClient.gql'

type ProductConfigurationType = {
  productGetByServer: Product
  className?: string
  cmsComponents: ReactNode
  productAccordion: ReactNode
}

const ProductConfiguration: FunctionComponent<ProductConfigurationType> = ({
  productGetByServer,
  className,
  cmsComponents,
  productAccordion,
}) => {
  const { data } = useQuery(ProductClientDocument, {
    variables: {
      filters: { url_key: { eq: productGetByServer.url_key } },
      pageSize: 1,
    },
    fetchPolicy: 'no-cache',
  })

  const isTablet = useIsMobileOrTablet()
  const addProductToCart = useAddProductToCart()
  const addMyMoveProduct = useAddMyMoveProductToCart()
  const { addNotification } = useContext(AlertNotificationContext)
  const { storeConfig, isEcommerce } = useContext(ConfigContext)
  const router = useRouter()

  const [product, setProduct] = useState<Product>({
    ...productGetByServer,
    stock_status: 'Loading',
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { addModal, removeModal } = useContext(ModalContext) as ModalContextType
  const { handleSubmit, setValue, control, watch, reset } = useForm<ProductConfigurationFormData>({
    defaultValues: getProductConfigurationDefaultValues(product),
  })
  const t = useTranslation()

  const selectedValues = watch()

  useEffect(() => {
    if (data?.products?.items && data.products?.items.length === 0) {
      setProduct({
        stock_status: data?.products?.items && data?.products?.items[0]?.stock_status,
        ...product,
      })
    }
  }, [data?.products?.items])

  useEffect(() => {
    const productClientData =
      data?.products?.items && data.products?.items.length > 0
        ? data?.products?.items[0]
        : undefined

    if (productClientData) {
      setProduct({ ...product, ...productClientData })
    }
  }, [data])

  useEffect(() => {
    reset(getProductConfigurationDefaultValues(product))
  }, [product])

  useEffect(() => {
    // add class
    document.querySelector('html')?.classList.add('product-page-main')

    return () => {
      document.querySelector('html')?.classList.remove('product-page-main')
    }
  }, [])

  const selectedVariantsForBundleMyMove = useMemo(() => {
    if (selectedValues.typeProduct !== 'BundleProduct') {
      return null
    }

    const { myMoveColor, braceletMyMoveColor, braceletMyMoveSize } = selectedValues

    if (!myMoveColor || !braceletMyMoveColor || !braceletMyMoveSize) {
      return null
    }

    return getSelectedVariantsForBundleProduct(
      product,
      myMoveColor,
      braceletMyMoveColor,
      braceletMyMoveSize,
    )
  }, [product, selectedValues])

  const selectedVariant = useMemo(() => {
    if (selectedValues.typeProduct !== 'ConfigurableProduct') {
      return null
    }

    if (!selectedValues.isBraceletMyMove) {
      const { configurableOption } = selectedValues

      if (!configurableOption) {
        return null
      }
      const option = product.configurable_options?.[0]
      const result = findVariant(product, [
        { code: option?.attribute_code ?? '', value: configurableOption },
      ])
      return result || null
    }

    // bracelet my move
    const { configurableOptionSize, configurableOptionColor } = selectedValues
    const result = findVariant(product, [
      { code: BRACELET_MY_MOVE_SIZE_ATTRIBUTE_NAME, value: configurableOptionSize },
      { code: BRACELET_MY_MOVE_COLOR_ATTRIBUTE_NAME, value: configurableOptionColor },
    ])

    return result
  }, [product, selectedValues])

  const assets: AssetType[] = useMemo(
    () =>
      sortImageGalleryOfProduct(product).map((media, index) => ({
        id: index,
        type: media?.__typename
          ? media.__typename === 'ProductVideo'
            ? 'video'
            : 'image'
          : 'image',
        file: media.file,
        src: media.file ? buildFullUrlMedia(`/media/catalog/product${media.file}`) : media.url,
        srcImage: media?.url ?? '',
        srcVideo: media?.video_content?.video_url ?? '',
        alt: media?.label ?? '',
        isIconique: product.isIconique,
        isNew: product.isNew,
        isExcluWeb: product.isExcluWeb,
        small_image: media?.small_image ?? '',
        thumbnail: media?.thumbnail ?? '',
        messika_mid_thumbnail: media?.messika_mid_thumbnail ?? '',
        messika_product_medium: media?.messika_product_medium ?? '',
        messika_product_large: media?.messika_product_large ?? '',
        roles: media?.roles || [],
      })) ?? [],
    [product],
  )

  const myMoveAsset: AssetType | null = useMemo(() => {
    if (!selectedVariantsForBundleMyMove) {
      return null
    }
    const img = getMyMoveCustomImage(product, selectedVariantsForBundleMyMove)
    return img ? convertImageToAssetType(img as Image) : null
  }, [selectedVariantsForBundleMyMove, product.media_gallery])

  const isConfigurableProduct = selectedValues.typeProduct === 'ConfigurableProduct'
  const isBraceletMyMove =
    selectedValues.typeProduct === 'ConfigurableProduct' && selectedValues.isBraceletMyMove
  const isSimpleProduct = selectedValues.typeProduct === 'SimpleProduct'
  const isMyMoveProduct = selectedValues.typeProduct === 'BundleProduct'

  const callbackAddProduct = (contextResponse) => {
    setIsLoading(false)
    if (!contextResponse.success) {
      addNotification(contextResponse.error)
      return
    }

    const storedValues = JSON.parse(window.localStorage.getItem('productValues') as any)
    const storedPosition = storedValues ? storedValues.position : 0

    const gtmCategoryObj = gtmCategory(product.gtm_categories)
    const sizeVariant = selectedVariant?.product?.sku ?? product.sku

    gtmPush({
      event: 'add_to_cart',
      ecommerce: {
        currency: product.price_range?.minimum_price?.regular_price?.currency,
        value: product.price_range?.minimum_price?.regular_price?.value,
        items: [
          {
            item_name: product.gtm_name,
            item_id: product.sku,
            price: product.price_range.minimum_price.regular_price.value,
            item_brand: 'Messika',
            item_list_id: storedValues ? storedValues.listId : 'produit',
            item_list_name: storedValues ? storedValues.listName : 'Produit',
            index: storedPosition ?? '',
            item_variant: sizeVariant,
            ...gtmCategoryObj,
          },
        ],
      },
    })

    const uuid = crypto.randomUUID()
    addModal({
      uuid,
      component: <ModalCart uuid={uuid} onClose={() => removeModal(uuid)} />,
    })
  }

  const onSubmit: SubmitHandler<ProductConfigurationFormData> = (data) => {
    setIsLoading(true)
    const storedValues = JSON.parse(window.localStorage.getItem('productValues') as any)
    const storedPosition = storedValues ? storedValues.position : 0
    const storedListId = storedValues ? storedValues.listId : ''
    const storedListName = storedValues ? storedValues.listName : ''

    let selectedProductOption:
      | {
          id: number
          value_string: string
        }
      | undefined

    if (data.typeProduct !== 'BundleProduct' && product.options?.[0] && data.productOption) {
      selectedProductOption = {
        id: product.options[0].option_id ?? 0,
        value_string: data.productOption.toString(),
      }
    }

    if (data.typeProduct === 'ConfigurableProduct' && selectedVariant?.product) {
      addProductToCart(
        selectedVariant.product,
        data.carving,
        product,
        selectedProductOption,
        1,
        storedPosition,
        storedListId,
        storedListName,
      )
        .then(callbackAddProduct)
        .catch((error) => {
          handleError(error, false)
        })
    }

    if (data.typeProduct === 'SimpleProduct') {
      addProductToCart(
        product,
        data.carving,
        undefined,
        selectedProductOption,
        1,
        storedPosition,
        storedListId,
        storedListName,
      )
        .then(callbackAddProduct)
        .catch((error) => {
          handleError(error, false)
        })
    }

    if (data.typeProduct === 'BundleProduct' && selectedVariantsForBundleMyMove) {
      const [myMoveVariant, braceletMyMoveVariant] = selectedVariantsForBundleMyMove

      if (myMoveVariant && braceletMyMoveVariant) {
        // TODO BundleProduct cart
        const options: AddMyMoveToCartOption[] = buildMyMoveOptionToCart(
          product,
          myMoveVariant,
          braceletMyMoveVariant,
        )

        addMyMoveProduct(product, options, storedPosition, storedListId, storedListName)
          .then(callbackAddProduct)
          .catch((error) => {
            handleError(error, false)
          })
      }
    }
  }

  const isCarving = selectedValues.typeProduct !== 'BundleProduct' && !!selectedValues.carving?.text

  const priceOfProduct = product.price_range?.minimum_price?.regular_price?.value || 0
  const priceOfSelectedVariant =
    selectedVariant?.product?.price_range?.minimum_price?.regular_price?.value || 0
  const priceOfSelectedVariantForBundleMyMove = getPriceOfSelectedVariantsForBundle(
    selectedVariantsForBundleMyMove,
  )

  const mariage = product.is_marriage
  const customPrice = product.display_custom_price

  useEffect(() => {
    if (!selectedVariant || !selectedVariant.product) {
      return
    }
    const inStock = getIsInStock(selectedVariant.product)
    if (inStock) {
      return
    }
    gtmPush({
      event: 'view_unavailable_item',
      ecommerce: {
        currency: product?.price_range?.minimum_price?.regular_price?.currency,
        value: product?.price_range?.minimum_price?.regular_price.value,
        items: [createGtmProduct({
          ...product,
          ...selectedVariant.product,
        })],
      },
    })
  }, [selectedVariant, product])

  return (
    <>
      <div className='container'>
        <Layout container={'padding'}>
          <>
            <BreadCrumb name={product.name ?? ''} breadcrumbs={[]} />
            <div className='product-page'>
              <section className='pictures'>
                {isMyMoveProduct ? (
                  <ProductAssets
                    assets={myMoveAsset ? [myMoveAsset] : [assets[0]]}
                    className='product-assets-my-move'
                    seeAllImagesByDefault
                  />
                ) : (
                  <>
                    {isBraceletMyMove ? (
                      <ProductAssets
                        assets={assets}
                        className='product-assets-my-move'
                        labelButton={t('catalog-product-image-see-more', {}, false)}
                        defaultNumberOfShowedAssets={3}
                      />
                    ) : (
                      <ProductAssets
                        assets={assets}
                        className='product-assets-standard'
                        seeAllImagesByDefault
                      />
                    )}
                  </>
                )}
                {isTablet && (
                  <Wishlist
                    productSku={product.sku ?? ''}
                    className='wishlist_button'
                    product={product}
                  />
                )}
              </section>
              <section className='product-informations'>
                <div className='container-product-informations'>
                  {!isTablet && (
                    <Wishlist
                      productSku={product.sku ?? ''}
                      className='wishlist_button'
                      product={product}
                    />
                  )}
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`product_configuration ${className}`}>
                    <div className='container-infos'>
                      <span className='title-h3 product-title'>{product.name}</span>
                      <div className='label-price'>
                        <h1
                          className='product-subtitle'
                          dangerouslySetInnerHTML={{ __html: product.title ?? '' }}
                        />
                        {storeConfig.display_prices === 1 && (
                          <div>
                            {mariage === 1 ? (
                              <>
                                {/* Si Mariage & "display_custom_price" -> affichage du prix "à partir de" */}
                                {customPrice === 1 ? (
                                  <ProductConfigurationPrice
                                    product={selectedVariant?.product ?? product}
                                    isStartingPrice
                                  />
                                ) : (
                                  // Si Mariage et "display_custom_price" === false -> affichaged'un libellé "prix sur demande"
                                  <ProductConfigurationPrice
                                    product={selectedVariant?.product ?? product}
                                    isCustomPrice
                                  />
                                )}
                              </>
                            ) : (
                              <>
                                {/* Si pas Mariage & "Bundle" -> affichage de "à partir de" + prix*/}
                                {isMyMoveProduct ? (
                                  <ProductConfigurationPriceBundleProduct
                                    product={product}
                                    selectedVariants={selectedVariantsForBundleMyMove}
                                  />
                                ) : (
                                  <ProductConfigurationPrice
                                    product={selectedVariant?.product ?? product}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {isMyMoveProduct && <MyMoveInformation />}

                    {!!product.product_link_options?.length && (
                      <CustomSelect
                        label=''
                        onChange={(url) => router.push(url)}
                        selectedValue={getUrlOfProductLinkOption(
                          product.product_link_options.find(
                            (item) => item?.product?.id === product.id,
                          ) ?? null,
                        )}
                        list={
                          product.product_link_options.map((item) => {
                            const urlProduct = getUrlOfProductLinkOption(item)

                            return {
                              title: item?.label ?? '',
                              value: urlProduct,
                              id: urlProduct,
                            }
                          }) ?? []
                        }
                      />
                    )}

                    {selectedValues.typeProduct === 'ConfigurableProduct' && (
                      <>
                        {selectedValues.isBraceletMyMove ? (
                          <ProductConfigurationBraceletMyMove
                            typeExtended={product.type_extended?.id || 0}
                            product={product}
                            size={selectedValues.configurableOptionSize}
                            onChangeSize={(value) => setValue('configurableOptionSize', value)}
                            color={selectedValues.configurableOptionColor}
                            onChangeColor={(value) => setValue('configurableOptionColor', value)}
                          />
                        ) : (
                          <ProductConfigurationSelectConfigurableOption
                            product={product}
                            value={selectedValues.configurableOption}
                            onChange={(value) => setValue('configurableOption', value)}
                          />
                        )}
                      </>
                    )}

                    {selectedValues.typeProduct === 'BundleProduct' && (
                      <ProductConfigurationBundleProduct
                        product={product}
                        myMoveColor={selectedValues.myMoveColor}
                        braceletMyMoveSize={selectedValues.braceletMyMoveSize}
                        braceletMyMoveColor={selectedValues.braceletMyMoveColor}
                        onChange={(name, value) => setValue(name, value)}
                      />
                    )}

                    {product.options?.[0] && selectedValues.typeProduct !== 'BundleProduct' && (
                      <Controller
                        name='productOption'
                        control={control}
                        render={({ field }) => (
                          <CustomSelect
                            label={product.options?.[0].title ?? ''}
                            placeholder=''
                            selectedValue={field.value}
                            onChange={field.onChange}
                            list={
                              product.options?.[0].value?.map((value) => ({
                                title: value?.title ?? '',
                                value: value?.option_type_id ?? 0,
                                id: value?.option_type_id ?? 0,
                              })) ?? []
                            }
                          />
                        )}
                      />
                    )}

                    {storeConfig.catalog_engraving_display_button &&
                      (isSimpleProduct || isConfigurableProduct) && (
                        <ProductConfigurationCarving
                          product={product}
                          selectedVariant={selectedVariant}
                          carvingValue={selectedValues.carving}
                          onChange={(value) => setValue('carving', value)}
                        />
                      )}

                    {isTablet && isEcommerce() && (
                      <ProductPaymentWidgets
                        amount={
                          priceOfSelectedVariantForBundleMyMove ||
                          priceOfSelectedVariant ||
                          priceOfProduct
                        }
                      />
                    )}

                    {!isTablet ? (
                      <div className='product_configuration-ctas'>
                        {isSimpleProduct && (
                          <ProductConfigurationCtaSimpleProduct
                            isLoading={isLoading}
                            product={product}
                            isCarving={isCarving}
                            onSubmit={handleSubmit(onSubmit)}
                          />
                        )}
                        {isConfigurableProduct && (
                          <ProductConfigurationCtaConfigurableProduct
                            isLoading={isLoading}
                            product={product}
                            selectedVariant={selectedVariant}
                            isCarving={isCarving}
                            onSubmit={handleSubmit(onSubmit)}
                            productOption={selectedValues.productOption}
                          />
                        )}
                        {isMyMoveProduct && (
                          <ProductConfigurationCtaBundleProduct
                            isLoading={isLoading}
                            product={product}
                            selectedVariants={selectedVariantsForBundleMyMove}
                            onSubmit={handleSubmit(onSubmit)}
                          />
                        )}
                        {isEcommerce() && (
                          <ProductPaymentWidgets
                            amount={
                              priceOfSelectedVariantForBundleMyMove ||
                              priceOfSelectedVariant ||
                              priceOfProduct
                            }
                          />
                        )}
                      </div>
                    ) : null}
                  </form>
                  {productAccordion}
                </div>
              </section>
            </div>
            {!isBraceletMyMove && !isMyMoveProduct && (
              <>
                <RecentViewedProducts currentProduct={product} />
                <div>
                  {product.related_products && product.related_products.length !== 0 && (
                    <div className='list-cross-sell-products'>
                      <ListSelection
                        items={mapCmsProductsToProducts({
                          items: product.related_products as any,
                        })}
                        listId={'vous aimerez surement'}
                        listName={'Suggestion page detail'}
                        name={
                          <h3 className='title-h3' data-aos='fade-up'>
                            {t('You may also like')}
                          </h3>
                        }
                        parentClassName='list-cross-sell-products'
                        centerInsufficientSlides
                        hideColorVariations
                        disableSwiperOfImages={isTablet}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        </Layout>
      </div>
      {cmsComponents}
      {isTablet ? (
        <section className='add-to-cart sticky-add-to-cart'>
          <div className='product_configuration-ctas'>
            {isSimpleProduct && (
              <ProductConfigurationCtaSimpleProduct
                isLoading={isLoading}
                product={product}
                isCarving={isCarving}
                onSubmit={handleSubmit(onSubmit)}
              />
            )}
            {isConfigurableProduct && (
              <ProductConfigurationCtaConfigurableProduct
                isLoading={isLoading}
                product={product}
                selectedVariant={selectedVariant}
                isCarving={isCarving}
                onSubmit={handleSubmit(onSubmit)}
                productOption={selectedValues.productOption}
              />
            )}
            {isMyMoveProduct && (
              <ProductConfigurationCtaBundleProduct
                isLoading={isLoading}
                selectedVariants={selectedVariantsForBundleMyMove}
                onSubmit={handleSubmit(onSubmit)}
                product={product}
              />
            )}
          </div>
        </section>
      ) : null}
    </>
  )
}

export default ProductConfiguration
